import React from 'react';
import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import './css/AnalyticsCharts.css';

const AnalyticsChart = ({
  type = 'line',
  data,
  title,
  lines = [],
  bars = [],
  height = 300,
  formatTooltip,
  xAxisKey
}) => {
  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    if (type === 'line') {
      return (
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey || 'x'} />
          <YAxis />
          <Tooltip formatter={formatTooltip} />
          <Legend />
          {lines.map((line, index) => (
            <Line
              key={index}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.color || '#3a7bd5'}
              strokeWidth={2}
              name={line.name}
            />
          ))}
        </LineChart>
      );
    }

    if (type === 'bar') {
      // Sort bars by order property if available
      const sortedBars = [...bars].sort((a, b) => (a.order || 0) - (b.order || 0));

      // Create custom legend content with the correct order
      const CustomLegend = ({ payload }) => {
        if (!payload || payload.length === 0) return null;

        // Sort the payload by our order
        const sortedPayload = [...payload].sort((a, b) => {
          const aBar = sortedBars.find(bar => bar.dataKey === a.dataKey);
          const bBar = sortedBars.find(bar => bar.dataKey === b.dataKey);
          return (aBar?.order || 0) - (bBar?.order || 0);
        });

        return (
          <div className="custom-legend" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
            marginTop: '10px'
          }}>
            {sortedPayload.map((entry, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: entry.color,
                  display: 'inline-block'
                }} />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {entry.value}
                </span>
              </div>
            ))}
          </div>
        );
      };

      return (
        <BarChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey || 'x'} />
          <YAxis />
          <Tooltip formatter={formatTooltip} />
          <Legend content={<CustomLegend />} />
          {sortedBars.map((bar, index) => (
            <Bar
              key={index}
              dataKey={bar.dataKey}
              fill={bar.color || '#3a7bd5'}
              name={bar.name}
            />
          ))}
        </BarChart>
      );
    }

    return null;
  };

  return (
    <div className="chart-container">
      <h3>{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

AnalyticsChart.propTypes = {
  type: PropTypes.oneOf(['line', 'bar']),
  data: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  xAxisKey: PropTypes.string,
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      color: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  bars: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      color: PropTypes.string,
      name: PropTypes.string,
      order: PropTypes.number,
    })
  ),
  height: PropTypes.number,
  formatTooltip: PropTypes.func,
};

const AnalyticsCharts = ({ charts }) => {
  return (
    <section className="analytics-charts">
      {charts.map((chart, index) => (
        <AnalyticsChart
          key={index}
          type={chart.type}
          data={chart.data}
          title={chart.title}
          xAxisKey={chart.xAxisKey}
          lines={chart.lines}
          bars={chart.bars}
          height={chart.height}
          formatTooltip={chart.formatTooltip}
        />
      ))}
    </section>
  );
};

AnalyticsCharts.propTypes = {
  charts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['line', 'bar']),
      data: PropTypes.array.isRequired,
      title: PropTypes.string.isRequired,
      lines: PropTypes.array,
      bars: PropTypes.array,
      height: PropTypes.number,
      formatTooltip: PropTypes.func,
    })
  ).isRequired,
};

export { AnalyticsChart, AnalyticsCharts };
export default AnalyticsCharts;
